import { computed, inject } from '@angular/core';
import { CustomerIncoterm, IncotermsDataService } from '@lib/incoterms';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

interface IDemandSupplierIncotermState {
    /** The system Incoterm catalog offered in the dropdown. */
    readonly incoterms: ReadonlyArray<CustomerIncoterm>;
    /** The explicit selection stored at the demand supplier, if any. */
    readonly incotermCode: string | null;
    /** The supplier default that applies while there is no explicit selection. */
    readonly defaultIncotermCode: string | null;
    /** True while the surrounding demand may be edited. */
    readonly editable: boolean;
}

const initialState: IDemandSupplierIncotermState = {
    incoterms: [],
    incotermCode: null,
    defaultIncotermCode: null,
    editable: false,
};

export const DemandSupplierIncotermStore = signalStore(
    withState(initialState),
    withComputed(({ incotermCode, defaultIncotermCode }) => ({
        /** The code in effect: the explicit selection, otherwise the supplier default. */
        effectiveIncotermCode: computed(() => incotermCode() ?? defaultIncotermCode()),

        /**
         * Removing only makes a visible difference while the explicit selection deviates from the
         * supplier default — the AI import stores a recognized code even when it matches that default.
         */
        canRemoveIncoterm: computed(() => !!incotermCode() && incotermCode() !== defaultIncotermCode()),
    })),
    withComputed(({ incoterms, effectiveIncotermCode, editable }) => ({
        /**
         * Dropdown selection: the catalog entry of the code in effect. A code the catalog no longer
         * knows — an Incoterm retired after the demand was created — is still shown as its bare code
         * instead of leaving the dropdown looking empty.
         */
        selectedIncoterm: computed(() => {
            const code = effectiveIncotermCode();
            if (!code) {
                return undefined;
            }
            return (
                incoterms().find((incoterm) => incoterm.shortCode === code) ?? new CustomerIncoterm({ shortCode: code })
            );
        }),

        displayIncoterm: computed(() => (editable() || !!effectiveIncotermCode()) && incoterms().length > 0),
    })),
    withComputed(({ selectedIncoterm, effectiveIncotermCode }) => ({
        selectedLabel: computed(() => selectedIncoterm()?.displayLabel ?? effectiveIncotermCode() ?? ''),
    })),
    withMethods((store, incotermsDataService = inject(IncotermsDataService)) => ({
        async loadIncoterms(): Promise<void> {
            patchState(store, { incoterms: await incotermsDataService.getIncoterms() });
        },

        /** Takes over the component inputs. */
        setSelection(incotermCode: string | null, defaultIncotermCode: string | null, editable: boolean): void {
            patchState(store, { incotermCode, defaultIncotermCode, editable });
        },
    })),
);
